import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { finalize, Observable, tap } from 'rxjs';
// Deep import: core is eagerly loaded and must not pull the zod schemas into the initial bundle.
import { Roles } from '@ric/interfaces/schemas/user/user.enum';
import { catchHttpError } from '@core/http/catch-http-error.operator';
import { HttpContentState } from '@core/http/http-content.state';
import { User } from '@core/users/models/user.model';
import { UserService } from '@core/users/services/user.service';
import { LoadCurrentUser } from './current-user.actions';
import { CurrentUserStateModel } from './current-user.model';

@State<CurrentUserStateModel>({
    name: 'currentUser',
    defaults: HttpContentState.defaults<User | null>(null)
})
@Injectable()
export class CurrentUserState extends HttpContentState {
    private readonly userService = inject(UserService);

    @Selector()
    public static roles(state: CurrentUserStateModel): Roles[] {
        return state.content?.roles ?? [];
    }

    @Selector()
    public static isAdmin(state: CurrentUserStateModel): boolean {
        return !!state.content?.roles?.includes(Roles.ADMIN);
    }

    @Selector()
    public static isArchived(state: CurrentUserStateModel): boolean {
        return !!state.content?.roles?.includes(Roles.ARCHIVED);
    }

    /** True once the backend lookup settled, so guards can decide without racing the request. */
    @Selector()
    public static isResolved(state: CurrentUserStateModel): boolean {
        return state.content !== null || state.error !== null;
    }

    @Selector()
    public static isAuthorized(state: CurrentUserStateModel): boolean {
        const roles = state.content?.roles ?? [];
        return roles.length > 0 && !roles.includes(Roles.ARCHIVED);
    }

    @Action(LoadCurrentUser)
    public loadCurrentUser(
        context: StateContext<CurrentUserStateModel>,
        { payload }: LoadCurrentUser
    ): Observable<User> {
        context.patchState({ isLoading: true, error: null });

        return this.userService.getUser(payload.gid).pipe(
            tap(content => context.patchState({ content })),
            catchHttpError(error => context.patchState({ error })),
            finalize(() => context.patchState({ isLoading: false }))
        );
    }
}
